import { useState } from "react";
import { Card } from "@/components/ui/card";

const koshas = [
  {
    name: "Annamaya",
    english: "Physical Body",
    meaning: "Anna = Food",
    description: "The gross physical sheath made of food",
    color: "from-amber-700 to-amber-800",
    bgColor: "bg-amber-700",
    borderColor: "border-amber-600",
    layer: 1,
  },
  {
    name: "Pranamaya",
    english: "Vital Energy",
    meaning: "Prana = Life Force",
    description: "The breath and energy body",
    color: "from-orange-600 to-orange-700",
    bgColor: "bg-orange-600",
    borderColor: "border-orange-500",
    layer: 2,
  },
  {
    name: "Manomaya",
    english: "Mental Body",
    meaning: "Manas = Mind",
    description: "Thoughts, emotions, desires",
    color: "from-sky-600 to-sky-700",
    bgColor: "bg-sky-600",
    borderColor: "border-sky-500",
    layer: 3,
  },
  {
    name: "Vijnanamaya",
    english: "Wisdom/Intellect",
    meaning: "Vijnana = Wisdom",
    description: "Discernment, intuition, higher knowing",
    color: "from-violet-600 to-violet-700",
    bgColor: "bg-violet-600",
    borderColor: "border-violet-500",
    layer: 4,
  },
  {
    name: "Anandamaya",
    english: "Bliss Body",
    meaning: "Ananda = Bliss",
    description: "Pure joy, closest to the soul",
    color: "from-amber-500 to-yellow-500",
    bgColor: "bg-amber-500",
    borderColor: "border-amber-400",
    layer: 5,
    darkText: true,
  },
];

const gunas = [
  {
    name: "Sattva",
    quality: "Clarity",
    manifestation: "Peace, wisdom, harmony",
    color: "bg-gradient-to-br from-white to-slate-100",
    textColor: "text-slate-700",
    borderColor: "border-slate-200",
    icon: "✧",
  },
  {
    name: "Rajas",
    quality: "Action",
    manifestation: "Passion, movement, desire",
    color: "bg-gradient-to-br from-red-500 to-orange-500",
    textColor: "text-white",
    borderColor: "border-red-400",
    icon: "⚡",
  },
  {
    name: "Tamas",
    quality: "Ignorance",
    manifestation: "Inertia, darkness, and delusion",
    color: "bg-gray-400",
    textColor: "text-white",
    borderColor: "border-gray-300",
    icon: "◯",
    forceWhite: true,
  },
];

const shivaShakti = [
  {
    name: "Shiva",
    nature: "Consciousness",
    quality: "Stillness",
    gender: "Masculine",
    role: "Witness",
    color: "bg-gradient-to-br from-blue-600 to-indigo-700",
    symbol: "☽",
  },
  {
    name: "Shakti",
    nature: "Energy",
    quality: "Movement",
    gender: "Feminine",
    role: "Creator",
    color: "bg-gradient-to-br from-rose-500 to-pink-600",
    symbol: "☼",
  },
];

const SpiritualDiagram = () => {
  const [activeKosha, setActiveKosha] = useState(null);
  const [activeGuna, setActiveGuna] = useState(null);

  return (
    <div className="space-y-16">
      {/* 3 Gunas Section */}
      <div className="space-y-6">
        <h3 className="text-2xl md:text-3xl font-serif text-primary text-center">
          The 3 Gunas
        </h3>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto">
          The three fundamental qualities of nature that govern all existence
        </p>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {gunas.map((guna, index) => (
            <Card
              key={guna.name}
              className={`${guna.color} ${guna.borderColor} border-2 p-6 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer`}
              onMouseEnter={() => setActiveGuna(index)}
              onMouseLeave={() => setActiveGuna(null)}
            >
              <div className={`text-3xl mb-2 ${guna.forceWhite ? 'text-white' : ''}`}>{guna.icon}</div>
              <h4 className={`text-xl font-serif font-bold ${guna.forceWhite ? 'text-white' : guna.textColor}`}>
                {guna.name}
              </h4>
              <p className={`text-sm font-semibold ${guna.forceWhite ? 'text-white' : guna.textColor} mt-1`}>
                {guna.quality}
              </p>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  activeGuna === index ? "max-h-20 mt-3 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <p className={`text-xs ${guna.forceWhite ? 'text-white' : guna.textColor} font-medium`}>
                  {guna.manifestation}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Connecting Arrow */}
      <div className="flex justify-center">
        <div className="w-px h-12 bg-gradient-to-b from-accent/60 to-primary/60" />
      </div>

      {/* 5 Koshas Section */}
      <div className="space-y-6">
        <h3 className="text-2xl md:text-3xl font-serif text-primary text-center">
          The 5 Koshas
        </h3>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto">
          The five sheaths of being, from gross physical to subtle bliss
        </p>
        
        {/* Nested Circles Visualization - Compact */}
        <div className="flex items-center justify-center py-4">
          <div className="relative w-64 h-64 md:w-72 md:h-72">
            {/* Annamaya - Outermost */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-700 to-amber-800 border-2 border-amber-600 shadow-lg" />
            {/* Pranamaya */}
            <div className="absolute inset-[10%] rounded-full bg-gradient-to-br from-orange-600 to-orange-700 border-2 border-orange-500 shadow-md" />
            {/* Manomaya */}
            <div className="absolute inset-[22%] rounded-full bg-gradient-to-br from-sky-600 to-sky-700 border-2 border-sky-500 shadow-md" />
            {/* Vijnanamaya */}
            <div className="absolute inset-[34%] rounded-full bg-gradient-to-br from-violet-600 to-violet-700 border-2 border-violet-500 shadow-md" />
            {/* Anandamaya - Innermost */}
            <div className="absolute inset-[46%] rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 border-2 border-amber-400 shadow-md flex items-center justify-center">
              <div className="text-center">
                <span className="text-[10px] md:text-xs font-serif font-bold text-amber-900">Atman</span>
              </div>
            </div>
          </div>
        </div>

        {/* Kosha Cards Grid - Compact 5-column layout */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 max-w-4xl mx-auto">
          {koshas.map((kosha, index) => {
            const isDark = kosha.darkText;
            const textColor = isDark ? 'text-amber-900' : 'text-white';
            const textShadow = isDark ? 'none' : '1px 1px 3px rgba(0,0,0,0.6)';
            return (
              <div
                key={kosha.name}
                className={`bg-gradient-to-br ${kosha.color} rounded-xl p-4 text-center shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer`}
                onMouseEnter={() => setActiveKosha(index)}
                onMouseLeave={() => setActiveKosha(null)}
              >
                <div className={`text-[11px] ${textColor} mb-1 uppercase tracking-wide font-bold`} style={{ textShadow }}>
                  LAYER {kosha.layer}
                </div>
                <h4 className={`text-base font-serif font-bold ${textColor}`} style={{ textShadow }}>{kosha.name}</h4>
                <p className={`text-sm ${textColor} font-medium mt-1`} style={{ textShadow }}>{kosha.english}</p>
                <div className={`overflow-hidden transition-all duration-300 ${activeKosha === index ? 'max-h-24 mt-2 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className={`text-xs ${textColor} font-medium italic`} style={{ textShadow }}>{kosha.meaning}</p>
                  <p className={`text-xs ${isDark ? 'text-amber-800' : 'text-white/90'} mt-1 leading-relaxed`} style={{ textShadow }}>{kosha.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Direction indicator */}
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <span>Outer</span>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
            <div className="w-2 h-2 rounded-full bg-sky-500" />
            <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
            <div className="w-1 h-1 rounded-full bg-yellow-400" />
          </div>
          <span>Inner (Soul)</span>
        </div>
      </div>

      {/* Connecting Arrow */}
      <div className="flex justify-center">
        <div className="w-px h-12 bg-gradient-to-b from-primary/60 to-accent/60" />
      </div>

      {/* Shiva-Shakti Section */}
      <div className="space-y-6">
        <h3 className="text-2xl md:text-3xl font-serif text-primary text-center">
          Shiva-Shakti
        </h3>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto">
          The divine union of consciousness and energy, masculine and feminine
        </p>
        
        <div className="flex justify-center">
          <div className="relative flex">
            {/* Yin-Yang style container */}
            <div className="flex rounded-full overflow-hidden border-4 border-accent/30 shadow-lg">
              {shivaShakti.map((aspect, index) => (
                <div
                  key={aspect.name}
                  className={`${aspect.color} p-6 md:p-8 text-white transition-all duration-300 hover:scale-105 cursor-pointer ${
                    index === 0 ? "rounded-l-full pl-8 md:pl-12" : "rounded-r-full pr-8 md:pr-12"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-3xl md:text-4xl mb-2">{aspect.symbol}</div>
                    <h4 className="text-xl md:text-2xl font-serif font-semibold">
                      {aspect.name}
                    </h4>
                    <div className="mt-3 space-y-1 text-sm opacity-90">
                      <p>{aspect.nature}</p>
                      <p>{aspect.quality}</p>
                      <p className="text-xs opacity-75">{aspect.gender} • {aspect.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Center union symbol */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg border-2 border-white/30">
              <span className="text-white text-lg md:text-xl font-serif">ॐ</span>
            </div>
          </div>
        </div>

        {/* Shiva-Shakti Details */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mt-8">
          {shivaShakti.map((aspect) => (
            <Card
              key={aspect.name}
              className="card-spiritual p-6 text-center hover:shadow-lg transition-shadow"
            >
              <h4 className="text-lg font-serif text-primary font-semibold mb-3">
                {aspect.name} Qualities
              </h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p><span className="font-medium">Nature:</span> {aspect.nature}</p>
                <p><span className="font-medium">Quality:</span> {aspect.quality}</p>
                <p><span className="font-medium">Energy:</span> {aspect.gender}</p>
                <p><span className="font-medium">Role:</span> {aspect.role}</p>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Integration Note */}
      <div className="text-center max-w-3xl mx-auto pt-8">
        <Card className="card-spiritual p-8 bg-gradient-to-br from-primary/5 to-accent/5">
          <h4 className="text-xl font-serif text-primary mb-4">
            The Sacred Integration
          </h4>
          <p className="text-muted-foreground leading-relaxed">
            These three frameworks interweave to form a complete map of human experience. 
            The <strong>Gunas</strong> describe the qualities that flow through all layers, 
            the <strong>Koshas</strong> reveal the sheaths we navigate from body to bliss, 
            and <strong>Shiva-Shakti</strong> represents the cosmic dance of stillness and movement 
            that animates all existence. Together, they guide the journey from fragmentation to wholeness.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default SpiritualDiagram;
